import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { CONFLUENCE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useCloudName } from '@atlassian/jira-tenant-context-controller/src/components/cloud-name/index.tsx';
import { invite as inviteService, mapInviteResult } from '@atlassian/expansion-services/services';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { AnyAri } from '@atlassian/ari/any-ari';
import { SOURCE, type Source } from '../services/feature-flag/index.tsx';
import { useInvitees } from '../services/get-invitees/index.tsx';
import { getPageTitle } from '../services/get-page-title/index.tsx';
import { useCanInvite } from '../services/invite-capability/index.tsx';
import { useInviteUsers } from '../services/invite/index.tsx';
import { useOptOut } from '../services/opt-out/index.tsx';
import { isConfluenceLink, type LinkData } from '../utils/index.tsx';
import { useIsSiteOptedOut } from '../services/opt-out/use-site-opt-out/index.tsx';
import { invitePromptOnLinkInsertExperience } from './experiences/index.tsx';
import { useInviteErrorFlag } from './invite-error-flag/index.tsx';
import { useInvitePromptFlag } from './invite-prompt-flag/index.tsx';
import { useInviteSuccessFlag } from './invite-success-flag/index.tsx';

export const useInvitePrompt = (createIssueProjectKey?: string) => {
	const canInvite = useCanInvite();
	const getInvitees = useInvitees(createIssueProjectKey);
	const showInvitePromptFlag = useInvitePromptFlag();
	const showSuccessFlag = useInviteSuccessFlag();
	const showErrorFlag = useInviteErrorFlag();
	const inviteUsers = useInviteUsers();
	const invite = useService(inviteService);
	const { getOptOutStatus, setOptOutStatus, incrementViewCount, shouldShowOptOutOption } =
		useOptOut();
	const isSiteOptedOut = useIsSiteOptedOut();
	return useCallback(
		async (continueUrl: string, data: LinkData, source: Source = SOURCE.ISSUE_VIEW) => {
			try {
				// eslint-disable-next-line jira/ff/unsafe-no-exposure
				const [config] = UNSAFE_noExposureExp('persistent_invite_on_link_insert');
				if (config.get('cohort', 'not-enrolled') === 'variation') return;

				invitePromptOnLinkInsertExperience.start();
				const { tenantId, product, name } = data;
				const hasUserOptedOut = await getOptOutStatus();
				if (hasUserOptedOut) {
					return;
				}

				const hasSiteOptedOut = await isSiteOptedOut();
				if (hasSiteOptedOut) {
					return;
				}

				const canInviteToProduct = await canInvite(tenantId, product);
				if (!canInviteToProduct) {
					return;
				}

				const users = await getInvitees(tenantId, product, 10);
				if (users.length === 0) {
					return;
				}

				const targetPageLabel = name || (await getPageTitle(continueUrl));

				const integration = 'invitePromptOnLink';

				const onSubmit = async () => {
					try {
						let requireAdminApproval = false;
						if (fg('expansion-services-in-aioli')) {
							const response = await invite({
								continueUrl,
								users: users.map(({ id }) => ({ id })),
								integration,
								touchpoint: 'Prompt invite on link insert',
								resources: [
									AnyAri.create({
										resourceOwner: product,
										resourceType: 'site',
										resourceId: tenantId,
									}),
								],
							});
							requireAdminApproval = mapInviteResult(response).requested.length > 0;
						} else {
							const response = await inviteUsers({
								continueUrl,
								userIds: users.map(({ id }) => id),
								tenantId,
								product,
								integration,
							});
							requireAdminApproval = response.accessRequested.length > 0;
						}

						showSuccessFlag({
							userIds: users.map(({ id }) => id),
							product,
							requireAdminApproval,
						});
					} catch {
						showErrorFlag();
					}
				};

				showInvitePromptFlag({
					users,
					product,
					source,
					targetPageLabel,
					allowOptOut: await shouldShowOptOutOption(),
					onSubmit,
					onDismiss: incrementViewCount,
					onOptOut: setOptOutStatus,
				});

				invitePromptOnLinkInsertExperience.success();
				return true;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'useInvitePromptExperiment',
						packageName: 'jiraInvitePromptOnLinkInsert',
					},
					error,
				});
				invitePromptOnLinkInsertExperience.failure();
			}
		},
		[
			canInvite,
			getInvitees,
			getOptOutStatus,
			incrementViewCount,
			invite,
			inviteUsers,
			isSiteOptedOut,
			setOptOutStatus,
			shouldShowOptOutOption,
			showErrorFlag,
			showInvitePromptFlag,
			showSuccessFlag,
		],
	);
};

export const useInviteOnCreateLink = (link: string) => {
	const cloudId = useCloudId();
	const cloudName = useCloudName();
	const triggerInviteExperiment = useInvitePrompt();
	return useCallback(() => {
		if (isConfluenceLink(link, cloudName)) {
			triggerInviteExperiment(link, { tenantId: cloudId, product: CONFLUENCE }, SOURCE.CREATE_LINK);
		}
	}, [cloudId, cloudName, link, triggerInviteExperiment]);
};
